import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import useAuth from '../../../../providers/Auth/Auth.provider';
import SettingsService from '../../../../providers/Settings/Settings.service';
import { AppSetting } from '../../../../providers/Settings/AppSettings.model';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import TwSwitch from '../../../../components/TwSwitch/TwSwitch';

const OrganizationSettings = (): JSX.Element => {
  const {
    updateTenantSetting,
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const enableLtl = userData?.tenant?.ltl_enabled;
  const enableDivisions = getTenantSetting('divisions');
  const enableOO = getTenantSetting('owner-operators');
  const enableMC = getTenantSetting('multiple-carriers');
  const [divisions, setDivisions] = useState<AppSetting | null>(null);
  const [ownerOperators, setOwnerOperators] = useState<AppSetting | null>(null);
  const [multipleCarriers, setMultipleCarriers] = useState<AppSetting | null>(null);
  const [enable, setEnable] = useState<boolean>(enableDivisions);
  const [enableOwnerOperators, setEnableOwnerOperators] = useState<boolean>(enableOO);
  const [enableMultipleCarriers, setEnableMultipleCarriers] = useState<boolean>(enableMC);

  useEffect(() => {
    (async (): Promise<void> => {
      const settings = await SettingsService.getAppSettings();

      // app settings return all, we are getting only divisions info
      const divisionSetting = settings.filter((setting) => setting.name === 'Divisions');
      const ownerOperatorsSetting = settings.filter(
        (setting) => setting.name === 'Owner Operators'
      );
      const multipleCarriersSetting = settings.filter(
        (setting) => setting.name === 'Multiple carriers'
      );
      setDivisions(divisionSetting[0]);
      setOwnerOperators(ownerOperatorsSetting[0]);
      setMultipleCarriers(multipleCarriersSetting[0]);
    })();
  }, []);

  const toggleDivisions = async (e: BaseSyntheticEvent): Promise<void> => {
    const checked = e.target.checked;
    if (!divisions) return;
    try {
      SettingsService.updateSettings({
        settings: [{ id: divisions.id, enabled: checked }]
      }).then(() => {
        updateTenantSetting({ [divisions.readableId]: checked });
        setEnable(checked);

        addNotification({
          message: 'Setting updated successfully',
          variant: 'success'
        });
      });
    } catch (err) {
      addNotification({
        message: 'Error while updating setting',
        variant: 'error'
      });
    }
  };

  const toggleOwnerOperators = async (e: BaseSyntheticEvent): Promise<void> => {
    const checked = e.target.checked;
    if (!ownerOperators) return;
    try {
      SettingsService.updateSettings({
        settings: [{ id: ownerOperators.id, enabled: checked }]
      }).then(() => {
        updateTenantSetting({ [ownerOperators.readableId]: checked });
        setEnableOwnerOperators(checked);

        addNotification({
          message: 'Setting updated successfully',
          variant: 'success'
        });
      });
    } catch (err) {
      addNotification({
        message: 'Error while updating setting',
        variant: 'error'
      });
    }
  };

  const toggleMultipleCarriers = async (e: BaseSyntheticEvent): Promise<void> => {
    const checked = e.target.checked;
    if (!multipleCarriers) return;

    try {
      await SettingsService.updateSettings({
        settings: [{ id: multipleCarriers.id, enabled: checked }]
      });

      updateTenantSetting({ [multipleCarriers.readableId]: checked });
      setEnableMultipleCarriers(checked);

      addNotification({
        message: 'Setting updated successfully',
        variant: 'success'
      });
    } catch (err) {
      addNotification({
        message: 'Error while updating setting',
        variant: 'error'
      });
    }
  };

  return (
    <Box>
      <Box className="app-settings__container">
        <Box>
          <Typography variant="h5" className="app-settings__subtitle">
            Organization
          </Typography>
          <Typography className="settings__subtitle">Manage your organization settings</Typography>
        </Box>
        <Box>
          <Stack sx={{ maxWidth: '500px' }}>
            <TwSwitch checked={enable} onChange={toggleDivisions} label="Divisions" />
            <Typography className="settings__subtitle app-settings__switch-description">
              For businesses with subsidiaries, use this setting to assign loads to specific
              divisions and enable the ability to bill directly from the corresponding division
              during invoicing.
            </Typography>
          </Stack>
          <Stack sx={{ maxWidth: '500px' }}>
            <TwSwitch
              checked={enableMultipleCarriers}
              onChange={toggleMultipleCarriers}
              label={multipleCarriers?.name}
            />
            <Typography className="settings__subtitle app-settings__switch-description">
              {multipleCarriers?.description}
            </Typography>
          </Stack>

          <Stack sx={{ maxWidth: '500px' }}>
            <TwSwitch
              checked={enableOwnerOperators}
              onChange={toggleOwnerOperators}
              label={ownerOperators?.name}
            />
            <Typography className="settings__subtitle app-settings__switch-description">
              {ownerOperators?.description}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
export default OrganizationSettings;
